import React, { FC, useCallback, useRef } from 'react';
import { ListActivePaymentDto, PaymentMethodDto, PaymentState } from '../../../../../../generated-api';
import classNames from 'classnames';
import CashButton from './CashButton';
import IntegratedTerminalPaymentButton from './IntegratedTerminalPaymentButton';
import UnintegratedPaymentButton from './UnintegratedPaymentButton';
import { useTerminalState } from '../../../../../context/PaymentMethodStore';
import { useF1NetworkState } from '../../../../../context/F1NetworkStateStore';

interface Props {
  paymentMethod: PaymentMethodDto;
  activePayment?: ListActivePaymentDto;
  initiatePayment: (
    paymentMethod: PaymentMethodDto,
    dynamicFields?: { [key: string]: string },
    promptTips?: boolean
  ) => void;
  setFocusedPayment: (activePayment: ListActivePaymentDto) => void;
  shouldGrow: boolean;
  canTryPaymentAnyway: boolean;
}

const PaymentButton: FC<Props> = ({
  paymentMethod,
  activePayment,
  initiatePayment,
  setFocusedPayment,
  shouldGrow,
  canTryPaymentAnyway,
}) => {
  const isCash = paymentMethod.paymentMethod == 'CASH';
  const terminalState = useTerminalState(paymentMethod.id);
  const { isOffline } = useF1NetworkState();

  const className = classNames({
    'flex-col ml-2 first-of-type:ml-4 last-of-type:mr-4': true,
    'opacity-75': terminalState === 'BUSY',
    'flex-1': shouldGrow && !isCash,
    'flex-none': !shouldGrow,
    'bg-grey-medium text-grey-dark border-grey-medium cursor-not-allowed': terminalState === 'OFFLINE' || isOffline,
  });
  const pendingRef = useRef(false);
  const handleButtonClick = useCallback(
    (ignoreTerminalState = false) => {
      if (isOffline) return;

      if (activePayment?.paymentState === PaymentState.AWAITING_PAYMENT) {
        setFocusedPayment(activePayment);
        return;
      }

      if (pendingRef.current) return;

      if (!ignoreTerminalState && terminalState !== 'AVAILABLE') return;

      pendingRef.current = true;
      setTimeout(() => (pendingRef.current = false), 500);

      initiatePayment(paymentMethod, undefined, true);
    },
    [activePayment, initiatePayment, isOffline, paymentMethod, setFocusedPayment, terminalState]
  );

  if (isCash) {
    return (
      <CashButton
        className={className}
        paymentMethod={paymentMethod}
        handleButtonClick={handleButtonClick}
        activePayment={activePayment}
        busy={terminalState === 'BUSY'}
      />
    );
  }
  if (paymentMethod.provider == 'UNINTEGRATED') {
    return (
      <UnintegratedPaymentButton
        paymentMethod={paymentMethod}
        onClick={initiatePayment}
        className={className}
        busy={terminalState === 'BUSY'}
      />
    );
  }

  return (
    <IntegratedTerminalPaymentButton
      paymentMethod={paymentMethod}
      terminalState={terminalState}
      className={className}
      onClick={() => handleButtonClick()}
      onTryAnyway={() => handleButtonClick(true)}
      canTryPaymentAnyway={canTryPaymentAnyway}
    />
  );
};

export default React.memo(PaymentButton);
